import type { User, UserManager } from "oidc-client-ts"
import { type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { AuthContext, type AuthContextValue, type AuthStatus } from "./AuthContext"
import { getUserManager } from "./userManager"

interface AuthProviderProps {
  readonly children: ReactNode
  /** Injected by tests, and by a host adapter once the shell contract lands. */
  readonly userManager?: UserManager
}

const displayNameOf = (user: User | null): string | undefined => {
  const profile = user?.profile

  return (profile?.name ?? profile?.preferred_username ?? profile?.email) || undefined
}

export const AuthProvider = ({ children, userManager }: AuthProviderProps): ReactElement => {
  const managerRef = useRef<UserManager>(userManager ?? getUserManager())
  const manager = managerRef.current

  const [status, setStatus] = useState<AuthStatus>("initializing")
  const [user, setUser] = useState<User | null>(null)

  useEffect(() => {
    let active = true
    let settled = false

    const adopt = (next: User | null): void => {
      if (!active) {
        return
      }

      settled = true
      setUser(next)
      setStatus(next && !next.expired ? "signedIn" : "signedOut")
    }

    /**
     * On the redirect callback route the stored user is still empty when this
     * effect runs, so the read below resolves to null while the code exchange
     * is in flight. Let the exchange's `userLoaded` win rather than having a
     * late null knock the session back to signed out.
     */
    const adoptInitial = (next: User | null): void => {
      if (next === null && settled) {
        return
      }

      adopt(next)
    }

    const forget = (): void => adopt(null)

    manager.events.addUserLoaded(adopt)
    manager.events.addUserUnloaded(forget)
    manager.events.addAccessTokenExpired(forget)

    manager
      .getUser()
      .then(adoptInitial)
      .catch(() => adoptInitial(null))

    return () => {
      active = false
      manager.events.removeUserLoaded(adopt)
      manager.events.removeUserUnloaded(forget)
      manager.events.removeAccessTokenExpired(forget)
    }
  }, [manager])

  const signIn = useCallback(async (): Promise<void> => {
    await manager.signinRedirect()
  }, [manager])

  const signOut = useCallback(async (): Promise<void> => {
    await manager.signoutRedirect()
  }, [manager])

  const clearToken = useCallback((): void => {
    manager.removeUser().catch(() => setStatus("signedOut"))
  }, [manager])

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      accessToken: user?.access_token,
      displayName: displayNameOf(user),
      userId: user?.profile?.sub,
      signIn,
      signOut,
      clearToken,
    }),
    [status, user, signIn, signOut, clearToken],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
